import type { IAvailability, IAvailabilityOverride, IAvailabilityOverrideInput, IWorkingHoursItem } from '@torim-app/types';
import { AvailabilityOverrideTypeEnum, Days, daysMapping, MAIN_BRANCH_ID } from '@torim-app/types';
import type { IAdminAvailabilityStore } from '@app/types';

import { defineStore } from 'pinia';
import { AvailabilityApi } from '@app/api/admin/availability-api';
import { AvailabilityOverridesApi } from '@app/api/admin/availability-overrides-api';
import { DateTime } from 'luxon';
import { getNumericHour } from '@torim-app/utils';
import { useAdminHomeStore } from './admin-main';

const toHourString = (h: IWorkingHoursItem['from']): string => (typeof h === 'string' ? h : h.name);

const mergeRanges = (ranges: IWorkingHoursItem[]): IWorkingHoursItem[] => {
  if (!ranges.length) return [];
  const sorted = ranges
    .map(r => ({ from: toHourString(r.from), to: toHourString(r.to) }))
    .sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
  const merged: Array<{ from: string; to: string }> = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    if (sorted[i].from <= last.to) {
      if (sorted[i].to > last.to) last.to = sorted[i].to;
    } else {
      merged.push(sorted[i]);
    }
  }
  return merged;
};

export const useAdminAvailabilityStore = defineStore('admin-availability', {
  state: (): IAdminAvailabilityStore => ({
    availability: {},
    overrides: []
  }),
  getters: {
    getAvailability(state): Record<string, IAvailability> {
      return state.availability;
    },
    getOverrides(state): IAvailabilityOverride[] {
      return state.overrides;
    },
    getOverridesByProvider(state) {
      return (providerId: string) => state.overrides.filter(o => o.providingClientId === providerId);
    },
    getAvailabilityByDay(state) {
      return (providerId: string, day: number) => {
        return state.availability?.[providerId]?.[daysMapping?.[day]] ?? [];
      };
    },
    /**
     * Distinct branch ids a provider works at across their weekly blocks.
     * Untagged blocks count as the main branch (mirrors the backend).
     */
    getProviderBranchIds(state) {
      return (providerId: string): string[] => {
        const availability = state.availability?.[providerId];
        if (!availability) return [];
        const ids = new Set<string>();
        for (const dayBlocks of Object.values(availability)) {
          for (const block of dayBlocks ?? []) {
            ids.add(block?.branchId || MAIN_BRANCH_ID);
          }
        }
        return [...ids];
      };
    },
    /**
     * Effective working hours for `(providerId, date)` — weekly schedule
     * unioned with any ADD overrides whose range covers that date.
     */
    getEffectiveHoursForDate(state) {
      return (providerId: string, date: Date): IWorkingHoursItem[] => {
        const dt = DateTime.fromJSDate(date);
        const weekday = daysMapping[dt.weekday];
        const dateStr = dt.toFormat('yyyy-MM-dd');
        const weekly = state.availability?.[providerId]?.[weekday] ?? [];
        const ranges: IWorkingHoursItem[] = [...weekly];
        for (const o of state.overrides) {
          if (o.providingClientId !== providerId) continue;
          if (o.type !== AvailabilityOverrideTypeEnum.ADD) continue;
          if (o.startDate > dateStr || o.endDate < dateStr) continue;
          if (o.daysOfWeek?.length && !o.daysOfWeek.includes(weekday)) continue;
          ranges.push(...o.hours);
        }
        return mergeRanges(ranges);
      };
    },
    getWorkingHoursRange() {
      return (providerId: string, selectedDate: Date) => {
        const todayAvailability = this.getEffectiveHoursForDate(providerId, selectedDate);
        if (todayAvailability.length === 0) return { start: undefined, end: undefined };

        const start = Math.min(...todayAvailability.map(avail => getNumericHour(toHourString(avail.from))));
        const end = Math.max(...todayAvailability.map(avail => getNumericHour(toHourString(avail.to))));
        return { start, end };
      };
    },
    getWorkingHoursRangeToday() {
      return (providerId: string) => {
        const adminStore = useAdminHomeStore();
        return this.getWorkingHoursRange(providerId, adminStore.selectedDateAtCalendar);
      };
    }
  },
  actions: {
    async updateBusinessAvailability(availability: IAvailability, providerClient?: string) {
      const updatedAvailability = await AvailabilityApi.updateAvailability({ availability, userId: providerClient });
      Object.assign(this.availability, updatedAvailability);
    },
    async fetchBusinessAvailability() {
      const availabilities = await AvailabilityApi.fetchAvailability();
      const sortOrder = [Days.SUNDAY, Days.MONDAY, Days.TUESDAY, Days.WEDNESDAY, Days.THURSDAY, Days.FRIDAY, Days.SATURDAY];
      for (const [providerId, availability] of Object.entries(availabilities)) {
        availabilities[providerId] = <IAvailability>Object.fromEntries(Object.entries(availability).sort((a, b) => {
          return sortOrder.indexOf(<Days>a[0]) - sortOrder.indexOf(<Days>b[0]);
        }));
      }
      Object.assign(this.availability, availabilities);
    },
    async fetchAvailabilityOverrides() {
      this.overrides = await AvailabilityOverridesApi.list();
    },
    async createAvailabilityOverride(input: IAvailabilityOverrideInput) {
      const created = await AvailabilityOverridesApi.create(input);
      this.overrides.push(created);
      return created;
    },
    async updateAvailabilityOverride(id: string, input: IAvailabilityOverrideInput) {
      const updated = await AvailabilityOverridesApi.update(id, input);
      const idx = this.overrides.findIndex(o => o._id === id);
      if (idx >= 0) this.overrides.splice(idx, 1, updated);
      return updated;
    },
    async deleteAvailabilityOverride(id: string) {
      await AvailabilityOverridesApi.remove(id);
      this.overrides = this.overrides.filter(o => o._id !== id);
    }
  }
});
